import { Alert, Skeleton } from "@digdir/designsystemet-react";
import { useQuery } from "@tanstack/react-query";
import { Link, useSearch } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { httpClient } from "@/api/client";
import type { GetMalerdataPerioderResponse } from "@/api/generated";
import { getMalerdataPerioderOptions } from "@/api/generated/@tanstack/react-query.gen";
import { Page } from "@/components";
import { getMonthStringFromDate } from "@/utils/dateTimeUtils";
import { useSelskaper } from "../../selskap/SelskapContext";
import { MaaledataPeriode } from "./MaaledataPeriode";

const ValidatePeriode = (
  validPeriods: GetMalerdataPerioderResponse,
  year: number,
  month: number,
) => {
  const date = new Date(year, month - 1, 1);

  const periodStarts = validPeriods.perioder.map(
    (p) => new Date(p.fraInklusiv),
  );
  const periodEnds = validPeriods.perioder.map((p) => new Date(p.tilEksklusiv));

  const earliest = periodStarts
    .slice()
    .sort((a, b) => a.getTime() - b.getTime())[0];
  const latest = periodEnds
    .slice()
    .sort((a, b) => b.getTime() - a.getTime())[0];

  const isValid = date >= earliest && date < latest;

  return isValid;
};

type PeriodeWithValidationProps = {
  målerdataRequest: {
    data: GetMalerdataPerioderResponse | undefined;
    isLoading: boolean;
    isError: boolean;
  };
  yearParam: number;
  monthParam: number;
  children?: ReactNode | ReactNode[];
};

const PeriodeWithValidation = ({
  målerdataRequest,
  yearParam,
  monthParam,
  children,
}: PeriodeWithValidationProps) => {
  const { t } = useTranslation();

  if (målerdataRequest.isLoading) {
    return (
      <Page>
        <Skeleton width={"100%"} height={200} />
      </Page>
    );
  }

  if (målerdataRequest.isError || !målerdataRequest.data) {
    return (
      <Page>
        <Alert data-color={"danger"}>
          {t("pages.maaledata-periode.feil.laste-feil")}
        </Alert>
      </Page>
    );
  }

  const valid = ValidatePeriode(målerdataRequest.data, yearParam, monthParam);

  if (!valid) {
    console.error("Tried to load målerdata periode with invalid params", {
      yearParam,
      monthParam,
    });
    return (
      <Page>
        <Alert data-color={"warning"}>
          {t("pages.maaledata-periode.feil.fant-ikke-periode", {
            context: `${getMonthStringFromDate(new Date(yearParam, monthParam - 1, 1))} ${yearParam}`,
          })}{" "}
          <Link to={"/maalerdata"}>
            {t("pages.maaledata-periode.feil.tilbake-link")}
          </Link>
        </Alert>
      </Page>
    );
  }

  return <>{children}</>;
};

export const MaaledataPeriodePage = () => {
  const { t } = useTranslation();
  const { active: organisasjonsNummer } = useSelskaper();
  const { month, year } = useSearch({
    from: "/_app/_subheader/maalerdata/periode",
  });

  const { data, isLoading, isError } = useQuery({
    ...getMalerdataPerioderOptions({
      client: httpClient,
      path: {
        // biome-ignore lint/style/noNonNullAssertion: Query disabled if null
        organisasjonsNummer: organisasjonsNummer!.organisasjonsnummer!,
      },
    }),
    enabled: organisasjonsNummer?.organisasjonsnummer != null,
  });

  return (
    <PeriodeWithValidation
      målerdataRequest={{ data, isLoading, isError }}
      yearParam={year}
      monthParam={month}
    >
      <Page withBackLink backLinkTo="/maalerdata">
        {organisasjonsNummer?.organisasjonsnummer != null ? (
          <MaaledataPeriode
            organisasjonsNummer={organisasjonsNummer.organisasjonsnummer}
            year={year}
            month={month}
          />
        ) : (
          <Alert data-color={"warning"}>
            <span>{t("pages.bestillinger.ingen-selskap")}</span>
            <br />
            <Link to="/selskap">{t("selskap-header.bytt-selskap")}</Link>
          </Alert>
        )}
      </Page>
    </PeriodeWithValidation>
  );
};
